import { useState } from "react";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { beatMax } from "../beatUtils";
import { Briefing } from "./Shared";
import { SlidersHorizontal, CheckCircle2 } from "lucide-react";

// Radiograph preview that brightens/darkens with exposure.
const Radiograph = ({ brightness, contrast, blur, label, tone }) => (
  <div className="relative rounded-lg overflow-hidden border-2 border-slate-700 bg-black" style={{ aspectRatio: "4 / 3" }} data-testid="xray-preview">
    <svg viewBox="0 0 200 150" className="w-full h-full" style={{ filter: `brightness(${brightness}%) contrast(${contrast}%) blur(${blur}px)` }}>
      <rect width="200" height="150" fill="#0b0b0b" />
      {/* spine */}
      <path d="M10 38 Q100 26 190 40" stroke="#cfd8e3" strokeWidth="10" fill="none" opacity="0.85" />
      {/* ribs */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <path key={i} d={`M${40 + i * 22} 44 Q${30 + i * 22} 95 ${60 + i * 22} 120`} stroke="#aeb8c4" strokeWidth="3" fill="none" opacity="0.6" />
      ))}
      {/* heart */}
      <ellipse cx="95" cy="92" rx="34" ry="28" fill="#e7edf3" opacity="0.55" />
      {/* diaphragm + sternum */}
      <path d="M120 120 Q150 110 188 116" stroke="#cfd8e3" strokeWidth="6" fill="none" opacity="0.7" />
      <path d="M55 122 Q100 132 150 124" stroke="#9aa6b2" strokeWidth="4" fill="none" opacity="0.5" />
    </svg>
    <span className="absolute bottom-1.5 left-1.5 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded" style={{ background: tone, color: "#fff" }} data-testid="xray-verdict">{label}</span>
  </div>
);

const Dial = ({ label, value, min, max, step, unit, onChange, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <span className="text-sm font-mono-clinical font-bold text-[#475569]">{value} {unit}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} data-testid={testid} className="w-full accent-[#475569]" />
  </div>
);

const inRange = (v, r) => v >= r.lo && v <= r.hi;

export function ExposureBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const kvR = beat.kv, masR = beat.mas, colR = beat.collimation;
  const [kv, setKv] = useState(Math.round((kvR.min + kvR.max) / 2));
  const [mas, setMas] = useState(Math.round(((masR.min + masR.max) / 2) * 10) / 10);
  const [col, setCol] = useState(80); // collimation coverage %
  const [res, setRes] = useState(null);
  const checked = !!res;

  const ratio = (kv * mas) / (kvR.ideal * masR.ideal);
  const brightness = Math.max(28, Math.min(225, Math.round(100 / ratio)));
  const off = Math.abs(Math.log(ratio));
  const blur = off > 0.6 ? Math.min(2.2, (off - 0.6) * 3) : 0;
  const diagnostic = ratio >= 0.82 && ratio <= 1.22 && inRange(col, colR);
  const verdict = diagnostic ? { label: "DIAGNOSTIC", tone: "#10b981" } : ratio < 0.82 ? { label: "UNDER-EXPOSED (too light)", tone: "#f59e0b" } : ratio > 1.22 ? { label: "OVER-EXPOSED (too dark)", tone: "#ef4444" } : { label: "CHECK COLLIMATION", tone: "#f59e0b" };

  const onPrimary = () => {
    if (!checked) {
      const kvPts = inRange(kv, kvR) ? 7 : 0;
      const masPts = inRange(mas, masR) ? 7 : 0;
      const colPts = inRange(col, colR) ? 6 : 0;
      setRes({ earned: kvPts + masPts + colPts, max: beatMax(beat), detail: `kV ${inRange(kv, kvR) ? "ok" : "off"} · mAs ${inRange(mas, masR) ? "ok" : "off"} · collimation ${inRange(col, colR) ? "ok" : "off"}`, key: beat.key, label: beat.label, diagnostic });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={SlidersHorizontal}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : "Take the exposure"}
      onPrimary={onPrimary}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      <div className="relative">
        <Radiograph brightness={brightness} contrast={diagnostic ? 115 : 100} blur={blur} label={verdict.label} tone={verdict.tone} />
        {/* live collimation box */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="border-2 border-amber-300/80 border-dashed" style={{ width: `${col}%`, height: `${col}%` }} data-testid="collimation-box" />
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
        <Dial label="kVp" value={kv} min={kvR.min} max={kvR.max} step={1} unit="kV" onChange={(v) => { setKv(v); checked && setRes(null); }} testid={`${beat.key}-kv`} />
        <Dial label="mAs" value={mas} min={masR.min} max={masR.max} step={0.5} unit="mAs" onChange={(v) => { setMas(v); checked && setRes(null); }} testid={`${beat.key}-mas`} />
        <Dial label="Collimation coverage" value={col} min={30} max={100} step={1} unit="%" onChange={(v) => { setCol(v); checked && setRes(null); }} testid={`${beat.key}-collimation`} />
      </div>

      {checked && (
        <div className={`rounded-md border p-3 text-sm flex items-start gap-2 ${res.diagnostic ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid={`${beat.key}-note`}>
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Target for this view: ~{kvR.ideal} kV, ~{masR.ideal} mAs, tight collimation ({colR.lo}–{colR.hi}%). Higher kV / lower mAs gives the long grey scale needed for lung detail while freezing motion.</span>
        </div>
      )}
    </BeatShell>
  );
}
